import React, { ReactElement, useEffect, useRef, useState } from "react";
import { createStyles, makeStyles, withStyles, Theme, Box } from "@material-ui/core";
import Checkbox, { CheckboxProps } from "@material-ui/core/Checkbox";
import Table, { TableProps } from "@material-ui/core/Table";
import TableCell from "@material-ui/core/TableCell";
import TableContainer, { TableContainerProps } from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon/ListItemIcon";
import LinkIcon from "@material-ui/icons/Link";
import ListItemText from "@material-ui/core/ListItemText/ListItemText";
import TableBody from "@material-ui/core/TableBody";
import clsx from "clsx";
import CustomMenu from "./Menu/CustomMenu";
import { tesaBlue, tesaBlue3, tesaGrey1, tesaGrey4 } from "../styles/colors";
import StyledTooltip from "./Wrapper/StyledTooltip";
import ProgressBar from "../pages/Requests/TestTab/TabbedDialog/TestCasesTab/ProgressBar";
import TestCasesOverviewTableFilterLabel from "./TableColumnFilter/TableColumnFilter";
import HistoricalTestCaseResponse from "../api/models/HistoricalTestCaseResponse";

type ScrollBehavior = "STICKY" | "SCROLL";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bold: {
      fontFamily: "Proxima Nova Bold",
    },
    checkboxCell: {
      textAlign: "center",
    },
    label: {
      color: tesaGrey1,
      fontFamily: "Proxima Nova Bold",
      fontSize: "0.875rem",
    },
    linkIcon: {
      height: "1rem",
      marginRight: "0.2rem",
      transform: "translateY(3px)",
      width: "1rem",
    },
    noPadding: {
      padding: 0,
    },
    renderWhitespace: {
      whiteSpace: "pre",
    },
    sortIcon: {
      color: tesaGrey1,
      "&:hover": {
        color: tesaBlue,
        cursor: "pointer",
      },
    },
    staticWidth: {
      width: "1rem",
    },
    sticky: ({ columns, stickyIndex }) => ({
      display: "flex",
      "& table:nth-child(1)": {
        background: "white",
        borderRight:
          stickyIndex != null && columns.length > stickyIndex + 1 ? "1px solid rgba(224, 224, 224, 1)" : "none",
        flex: "1 0 25%",
        left: 0,
        minWidth: "57.5ch",
        position: "sticky",
        tableLayout: "fixed",
        zIndex: theme.zIndex.appBar,
      },
      "& table:nth-child(2)": {
        flex: `${
          stickyIndex != null && columns.length <= stickyIndex + 1 ? 0 : columns.length - (stickyIndex || 0)
        } 0 0`,
      },
      "& tbody .MuiTableRow-root": {
        height: "41px",
      },
      "& thead .MuiButtonBase-root.MuiTableSortLabel-root": {
        verticalAlign: "baseline",
      },
      "& .bg-blue": {
        backgroundColor: tesaBlue3,
        "& .LEAPActionCell": {
          color: tesaBlue,
        },
      },
    }),
    table: (props: CustomTableProps) => {
      const reducedPadding = {
        "& .MuiTableCell-root": {
          paddingLeft: "8px",
          paddingRight: "8px",
        },
      };
      if (props.stickyIndex != null && props.columns.length > 8) {
        return {
          marginBottom: theme.spacing(3),
          tableLayout: "auto",
          ...(props.reducePadding ? reducedPadding : {}),
        };
      }
      return {
        marginBottom: theme.spacing(3),
        tableLayout: props.hasFixedColumns ? "fixed" : "auto",
        ...(props.reducePadding ? reducedPadding : {}),
      };
    },
    tableRow: {
      "&:hover": {
        backgroundColor: [tesaBlue3, "!important"].join(" "),
        "& .MuiTableCell-root, & .MuiListItemText-primary": {
          fontSize: "0.875rem !important",
        },
        "& .LEAPActionCell": {
          color: tesaBlue,
          cursor: "pointer",
        },
      },
      "& .MuiTableCell-root, & .MuiListItemText-primary": {
        fontSize: "0.875rem",
        lineHeight: "1rem",
        paddingBottom: "0.5rem",
        paddingTop: "0.5rem",
      },
      borderBottom: `1px solid ${tesaGrey4}`,
    },
    tableHead: (props: CustomTableProps) => ({
      "& .MuiTableCell-head": props.headDense
        ? {
            borderTop: props.headBorderTop ? `1px solid ${tesaGrey4}` : 0,
            paddingBottom: "0.5rem",
            paddingTop: "0.5rem",
          }
        : {
            borderTop: props.headBorderTop ? `1px solid ${tesaGrey4}` : 0,
          },
    }),
  }),
);

const StyledMenuItem = withStyles((theme) => ({
  root: {
    "&:focus": {
      backgroundColor: tesaBlue,
      "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

function NoDataRow({ withFilters, colCount }: { colCount: number; withFilters?: boolean }) {
  return (
    <TableRow>
      <TableCell align="center" colSpan={colCount}>
        {withFilters ? "No results found. Please adjust your filters" : "No results found."}
      </TableCell>
    </TableRow>
  );
}

function CheckboxCell({ className, CheckboxProps = {} }: { className?: string; CheckboxProps?: CheckboxProps }) {
  return (
    <TableCell width="60px" className={clsx([className])} component="td">
      <Checkbox {...CheckboxProps} />
    </TableCell>
  );
}

export interface MenuAction {
  action: (rowData: CustomTableRowData) => void;
  icon?: ReactElement;
  text: string;
  show?: (rowData: CustomTableRowData) => boolean;
}

export type CustomTableRowData = { [key: string]: any } & {
  id: number;
};

export interface ColumnFilterObject {
  allowColumnFilters: boolean;
  resetColumnFilters: boolean;
  setResetColumnFilters: (v: boolean) => void;
  columnFilterMap: Map<string, string>;
}

interface ColumnFilter {
  [columnName: string]: string[];
}

const initialHistoricalColumnFilter: ColumnFilter = {
  testSeriesLabel: [],
  firstSubstrateSample: [],
  secondSubstrateSample: [],
  productSample: [],
  testMethodName: [],
  buildingAction: [],
  treatment: [],
  condition: [],
  completed: [],
};

interface CustomTableProps extends TableProps {
  boldIndex?: number;
  checkboxSelection?: boolean;
  columnMaxWidths?: string[] | Record<string, string>;
  columnExactWidths?: string[] | Record<string, string>;
  columns: string[];
  actionColumnIndexes?: number[] | "ALL";
  fillEmpty?: string | number;
  formatDates?: boolean;
  handleEdit?: (rowData: CustomTableRowData, key: string, rowIndex: number) => void;
  hasFixedColumns?: boolean;
  hasRowNumbers?: boolean;
  header?: string;
  headBorderTop?: boolean;
  headDense?: boolean;
  onCheckboxChange?: (checked: boolean, data: CustomTableRowData) => void;
  onSelectAllChange?: (checked: boolean, activeRows: HistoricalTestCaseResponse[]) => void;
  reducePadding?: boolean;
  renderWhitespaceColumns?: Record<number, boolean>;
  rowMenuActions?: MenuAction[];
  rowMenus?: boolean;
  rowNumbersHeader?: string;
  rowOrderFixed?: boolean;
  rows: any;
  selectedItemNameValue?: string;
  selectedRows?: Record<number, boolean>;
  sortableColumns?: {
    all?: boolean;
    [K: number]: boolean;
  };
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
  sortFunction?: Function;
  stickyIndex?: number;
  excludedKeys?: string[];
  TableContainerProps?: TableContainerProps;
  handleMultiTab?: (data: CustomTableRowData) => void;
  columnFilterObject?: ColumnFilterObject;
  onPriorityChange?: (id: string, priority: string) => void;
}

const CustomTable = (props: CustomTableProps): ReactElement => {
  const classes = useStyles(props);
  const {
    actionColumnIndexes = [],
    boldIndex,
    checkboxSelection,
    columnMaxWidths,
    columnExactWidths,
    columns,
    fillEmpty,
    handleEdit,
    hasFixedColumns,
    hasRowNumbers,
    header,
    onCheckboxChange,
    onSelectAllChange,
    reducePadding,
    renderWhitespaceColumns,
    rows,
    rowMenuActions,
    rowMenus,
    rowNumbersHeader,
    rowOrderFixed,
    selectedRows,
    sortableColumns,
    sortColumn,
    sortDirection,
    sortFunction,
    stickyIndex,
    excludedKeys,
    TableContainerProps,
    handleMultiTab,
    columnFilterObject,
    ...restProps
  } = props;

  const { className: classNameFromProps } = restProps;

  const customMenuRef = useRef<{ close: () => void }>(null);

  const sizes = columns.map((column, index) => ({
    maxWidth: Array.isArray(columnMaxWidths) ? columnMaxWidths?.[index] : columnMaxWidths?.[column],
    width: Array.isArray(columnExactWidths) ? columnExactWidths?.[index] : columnExactWidths?.[column],
  }));

  const [activeRows, setActiveRows] = useState(rows);
  const [columnFilters, setColumnFilters] = useState<ColumnFilter>(initialHistoricalColumnFilter);
  const [columnFilterOptions, setColumnFilterOptions] = useState<ColumnFilter>(initialHistoricalColumnFilter);

  useEffect(() => {
    const uniqueValuesByColumn: any = {};

    rows.forEach((row: any) => {
      Object.keys(columnFilters).forEach((key) => {
        if (!uniqueValuesByColumn[key]) {
          uniqueValuesByColumn[key] = new Set<string[]>();
        }
        if (row[key]) {
          uniqueValuesByColumn[key].add(row[key]);
        }
      });
    });

    const _columnFilters: ColumnFilter = {};
    const _columnFilterOptions: ColumnFilter = {};

    Object.keys(uniqueValuesByColumn).forEach((key) => {
      _columnFilters[key] = [];
      _columnFilterOptions[key] = [...uniqueValuesByColumn[key]];
    });

    setColumnFilters(_columnFilters);
    setColumnFilterOptions(_columnFilterOptions);
  }, [rows, selectedRows]);

  useEffect(() => {
    let filteredRows = [...rows];
    // eslint-disable-next-line no-restricted-syntax
    for (const key in columnFilters) {
      // eslint-disable-next-line no-prototype-builtins
      if (columnFilters.hasOwnProperty(key) && columnFilters[key].length) {
        filteredRows = filteredRows.filter((row) => {
          return columnFilters[key].includes(row[key]);
        });
      }
    }
    setActiveRows(filteredRows);
    setColumnFilters(columnFilters);
  }, [rows, selectedRows, columnFilters]);

  useEffect(() => {
    if (columnFilterObject?.resetColumnFilters) {
      setColumnFilters(initialHistoricalColumnFilter);
      columnFilterObject.setResetColumnFilters(false);
    }
  }, [columnFilterObject?.resetColumnFilters]);

  const renderCellContents = (rowData: CustomTableRowData, key: string) => {
    if (rowData[key] === true) {
      return "Yes";
    }
    if (rowData[key] === false) {
      return "No";
    }
    if (!rowData[key] && rowData[key] !== 0) {
      return fillEmpty ?? "";
    }
    if (typeof rowData[key] !== "string") {
      return rowData[key];
    }
    return (
      <StyledTooltip title={rowData[key]}>
        <span>{rowData[key]}</span>
      </StyledTooltip>
    );
  };

  const renderRowNumber = (
    config:
      | {
          type: "HEAD";
        }
      | { type: "BODY"; rowData: CustomTableRowData; rowIndex: number },
  ): JSX.Element | null => {
    if (!hasRowNumbers) {
      return null;
    }
    return config.type === "HEAD" ? (
      <TableCell
        align="left"
        component="td"
        className={hasFixedColumns ? [classes.label, classes.staticWidth].join(" ") : classes.label}
        style={{ width: "10px" }}
      >
        {rowNumbersHeader || null}
      </TableCell>
    ) : (
      <TableCell scope="row" align="left">
        {/* automatically count row numbers if not explicitly defined */}
        {config.rowData.rowNumber ?? config.rowIndex + 1}
      </TableCell>
    );
  };

  const getSize = (index: number) => (stickyIndex != null ? undefined : sizes[index]);

  const normalizeIndex = (index: number, scrollBehavior?: ScrollBehavior) => {
    let normalized = index;
    if (stickyIndex != null && scrollBehavior === "SCROLL") {
      normalized = stickyIndex + 1 + index;
    }
    return normalized;
  };

  const cellsFromRowData = (
    rowData: CustomTableRowData,
    rowIndex: number,
    keys: string[],
    scrollBehavior?: ScrollBehavior,
  ) =>
    keys.map((key, index) => {
      const indexNormalized = normalizeIndex(index, scrollBehavior);
      const isActionColumn = actionColumnIndexes === "ALL" || actionColumnIndexes.includes(indexNormalized);
      if (key === "creationDate") {
        rowData[key] = rowData[key].split("-").reverse().join(".");
      }
      if (key === "progress") {
        return (
          <TableCell component="td" style={getSize(indexNormalized)} key={key}>
            <ProgressBar completed={rowData[key].completed} total={rowData[key].total} />
          </TableCell>
        );
      }
      if (key !== "rowNumber") {
        return (
          <TableCell
            className={`${indexNormalized === boldIndex ? classes.bold : ""} ${
              isActionColumn && handleEdit && rowData[key] != null ? "LEAPActionCell" : ""
            } ${renderWhitespaceColumns?.[indexNormalized] ? classes.renderWhitespace : ""}`}
            component="td"
            style={getSize(indexNormalized)}
            key={key}
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey) {
                if (handleMultiTab) {
                  handleMultiTab(rowData);
                  return;
                }
              }

              if (key === "priority" && props.onPriorityChange) {
                e.stopPropagation();
                props.onPriorityChange(rowData.requestId, rowData.priority);
                return;
              }

              if (isActionColumn && handleEdit) {
                handleEdit(rowData, key, rowIndex);
              }
            }}
          >
            {rowData.linkedParent && index === 1 ? <LinkIcon className={classes.linkIcon} /> : null}
            {renderCellContents(rowData, key)}
          </TableCell>
        );
      }
      return null;
    });

  const getIncludedKeys = (rowData: CustomTableRowData) => {
    return excludedKeys?.length
      ? Object.keys(rowData).filter((key) => !excludedKeys.includes(key))
      : Object.keys(rowData);
  };

  const renderBodyCells = (
    rowData: CustomTableRowData,
    rowIndex: number,
    scrollBehavior?: ScrollBehavior,
  ): JSX.Element => {
    let keys = rowOrderFixed ? columns : getIncludedKeys(rowData);
    if (stickyIndex != null && scrollBehavior === "STICKY") {
      keys = keys.slice(0, stickyIndex + 1);
    }
    if (stickyIndex != null && scrollBehavior === "SCROLL") {
      keys = keys.slice(stickyIndex + 1);
    }
    return (
      <>
        {renderRowNumber({ type: "BODY", rowData, rowIndex })}
        {cellsFromRowData(rowData, rowIndex, keys, scrollBehavior)}
      </>
    );
  };

  const getHoverWholeRowProps = (rowIndex: number, scrollBehavior?: ScrollBehavior) => {
    if (!scrollBehavior) {
      return {};
    }
    const reverseRole = scrollBehavior === "STICKY" ? "SCROLL" : "STICKY";
    const rowSelector = `#LeapTable-${reverseRole} tbody tr:nth-child(${rowIndex + 1})`;
    return {
      onMouseEnter: () => {
        document.querySelector(rowSelector)?.classList.add("bg-blue");
      },
      onMouseLeave: () => {
        document.querySelector(rowSelector)?.classList.remove("bg-blue");
      },
    };
  };

  const renderBodyRow = (
    rowData: CustomTableRowData,
    rowIndex: number,
    scrollBehavior?: "STICKY" | "SCROLL",
  ): ReactElement => {
    const shownRowMenuActions = (rowMenuActions || []).filter((action) => {
      if (action.show) {
        return action.show(rowData);
      }
      return true;
    });
    return (
      <TableRow
        key={rowData.id ?? rowIndex}
        hover
        className={classes.tableRow}
        {...getHoverWholeRowProps(rowIndex, scrollBehavior)}
      >
        {renderBodyCells(rowData, rowIndex, scrollBehavior)}
        {rowMenus ? (
          <td>
            <div style={{ float: "right" }}>
              <CustomMenu disabled={shownRowMenuActions.length === 0} ref={customMenuRef}>
                {shownRowMenuActions.map((menuAction: MenuAction) => {
                  return (
                    <div key={menuAction.text}>
                      <StyledMenuItem
                        onClick={(): void => {
                          customMenuRef.current?.close();
                          menuAction.action(rowData);
                        }}
                      >
                        <ListItemIcon>{menuAction.icon}</ListItemIcon>
                        <ListItemText primary={menuAction.text} />
                      </StyledMenuItem>
                    </div>
                  );
                })}
              </CustomMenu>
            </div>
          </td>
        ) : (
          <></>
        )}
        {!!checkboxSelection && (
          <CheckboxCell
            className={classes.checkboxCell}
            CheckboxProps={{
              checked: !!selectedRows?.[rowData.id],
              color: "secondary",
              className: classes.noPadding,
              onChange: (_unused, checked) => onCheckboxChange?.(checked, rowData),
            }}
          />
        )}
      </TableRow>
    );
  };

  const cellsFromColumns = (cols: string[], startIndex?: number) => {
    return cols.map((column, index) => {
      const indexOffset = startIndex != null ? startIndex + index : index;
      const columnFilterKey = columnFilterObject?.columnFilterMap.get(column)!;

      if (sortableColumns?.all || sortableColumns?.[indexOffset]) {
        return (
          <TableCell key={`${column}-${indexOffset}`}>
            <TableSortLabel
              active={sortColumn === column?.toUpperCase() || sortColumn === column}
              direction={sortDirection}
              onClick={() => {
                if (sortFunction) {
                  sortFunction(column);
                } else {
                  console.warn("Provide sortFunction to sort rows.");
                }
              }}
            >
              <Box component="span">{column}</Box>
            </TableSortLabel>
          </TableCell>
        );
      }
      return (
        <TableCell key={`${column}-${indexOffset}`}>
          <Box component="span">{column}</Box>
          {columnFilterObject?.allowColumnFilters && (
            <TestCasesOverviewTableFilterLabel
              filterOptions={columnFilterOptions[columnFilterKey]}
              activeFilters={columnFilters[columnFilterKey]}
              popoverId={column}
              onFilterChange={(data) => {
                setColumnFilters({ ...columnFilters, [columnFilterKey]: data });
              }}
            />
          )}
        </TableCell>
      );
    });
  };

  const renderHeadRow = (columnNames: string[], startIndex?: number) => {
    return (
      <>
        {renderRowNumber({ type: "HEAD" })}
        {cellsFromColumns(columnNames, startIndex)}
      </>
    );
  };

  return (
    <>
      {header && <h3>{header}</h3>}
      <TableContainer
        {...(TableContainerProps || {})}
        style={columnFilterObject?.allowColumnFilters ? { height: 460, overflow: "auto" } : {}}
        className={clsx([TableContainerProps?.className, stickyIndex != null && classes.sticky])}
      >
        {stickyIndex != null ? (
          <>
            <Table
              id="LeapTable-STICKY"
              className={clsx([classes.table, classNameFromProps])}
              aria-label="product table"
            >
              <TableHead className={classes.tableHead}>
                <TableRow>
                  {renderHeadRow(columns.slice(0, stickyIndex + 1))}
                  {rowMenus && <TableCell align="left" className={classes.label} style={{ width: "10px" }} />}
                  {!!checkboxSelection && (
                    <CheckboxCell
                      className={classes.checkboxCell}
                      CheckboxProps={{
                        checked:
                          !!activeRows.length && activeRows.every((r: CustomTableRowData) => !!selectedRows?.[r.id]),
                        color: "secondary",
                        className: classes.noPadding,
                        indeterminate: true,
                        onChange: (_unused, checked) => {
                          onSelectAllChange?.(checked, activeRows);
                        },
                      }}
                    />
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {activeRows.length ? (
                  activeRows.map((rowData: any, index: number) => renderBodyRow(rowData, index, "STICKY"))
                ) : (
                  <NoDataRow withFilters={columnFilterObject?.allowColumnFilters} colCount={columns.length} />
                )}
              </TableBody>
            </Table>
            <Table
              id="LeapTable-SCROLL"
              className={clsx([classes.table, classNameFromProps])}
              aria-label="product table"
            >
              <TableHead className={classes.tableHead}>
                <TableRow>
                  {renderHeadRow(columns.slice(stickyIndex + 1), stickyIndex + 1)}
                  {rowMenus && <TableCell align="left" className={classes.label} style={{ width: "10px" }} />}
                  {!!checkboxSelection && (
                    <CheckboxCell
                      className={classes.checkboxCell}
                      CheckboxProps={{
                        checked:
                          !!activeRows.length && activeRows.every((r: CustomTableRowData) => !!selectedRows?.[r.id]),
                        color: "secondary",
                        className: classes.noPadding,
                        indeterminate: true,
                        onChange: (_unused, checked) => {
                          onSelectAllChange?.(checked, activeRows);
                        },
                      }}
                    />
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {activeRows.length ? (
                  activeRows.map((rowData: any, index: number) => renderBodyRow(rowData, index, "SCROLL"))
                ) : (
                  <NoDataRow withFilters={columnFilterObject?.allowColumnFilters} colCount={columns.length} />
                )}
              </TableBody>
            </Table>
          </>
        ) : (
          <Table className={clsx([classes.table, classNameFromProps])} aria-label="product table">
            <TableHead className={classes.tableHead}>
              <TableRow>
                {renderHeadRow(columns)}
                {rowMenus && <TableCell align="left" className={classes.label} style={{ width: "10px" }} />}
                {!!checkboxSelection && (
                  <CheckboxCell
                    className={classes.checkboxCell}
                    CheckboxProps={{
                      checked:
                        !!activeRows.length && activeRows.every((r: CustomTableRowData) => !!selectedRows?.[r.id]),
                      color: "secondary",
                      className: classes.noPadding,
                      indeterminate: true,
                      onChange: (_unused, checked) => {
                        onSelectAllChange?.(checked, activeRows);
                      },
                    }}
                  />
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {activeRows.length ? (
                activeRows.map((rowData: any, index: number) => renderBodyRow(rowData, index))
              ) : (
                <NoDataRow withFilters={columnFilterObject?.allowColumnFilters} colCount={columns.length} />
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </>
  );
};
export default CustomTable;
